import React, { FC, useContext, useEffect } from 'react';
import { FormProvider, useForm } from 'react-hook-form';
import { useNavigate } from 'react-router-dom';

import { AppContext } from '../../containers/appContextContainer';
import { EgenkapitalType, EgenkapitalWeb, ValgtWeb } from '../../generated-types';
import { useEgenkapital, useSaveEgenkapital } from '../../hooks/useEgenkapital';
import { useTranslation } from '../../translations';
import PageCard from '../page/pageCard';
import PageFooter from '../page/pageFooter';
import EgenkapitalAnnen from './egenkapitalAnnen';

export type EgenkapitalFormData = {
    harAnnenEgenkapital: ValgtWeb;
    annenEgenkapitalSum: number | undefined;
    kilde: EgenkapitalType[];
};

interface Props {
    navigateTo: string;
    navigateFrom: string;
}

const Egenkapital: FC<Props> = ({ navigateTo, navigateFrom }) => {
    const navigate = useNavigate();
    const { egenkapital: egenkapitalTrans } = useTranslation();
    const { soeknadstype } = useContext(AppContext);
    const { data: egenkapital } = useEgenkapital(soeknadstype);
    const { mutate: saveEgenkapital, isPending: isSavingEgenkapital } = useSaveEgenkapital(egenkapital, () => {
        navigate(navigateTo);
    });
    const methods = useForm<EgenkapitalFormData>({
        defaultValues: {
            harAnnenEgenkapital: ValgtWeb.IKKE_VALGT,
            annenEgenkapitalSum: undefined,
            kilde: [],
        },
    });
    const { reset, handleSubmit } = methods;

    useEffect((): void => {
        if (egenkapital) {
            reset({
                harAnnenEgenkapital: egenkapital.harAnnenEgenkapital,
                annenEgenkapitalSum: egenkapital.annenEgenkapitalSum || undefined,
                kilde: egenkapital.kilde || [],
            });
        }
    }, [egenkapital, reset]);

    const onSubmitEgenkapital = (data: EgenkapitalFormData) => {
        if (egenkapital) {
            const newEgenkapital: EgenkapitalWeb = {
                ...egenkapital,
                brukBsu: false,
                bsuSum: null,
                harAnnenEgenkapital: data.harAnnenEgenkapital,
                annenEgenkapitalSum: data.harAnnenEgenkapital === ValgtWeb.JA ? data.annenEgenkapitalSum || null : null,
                kilde: data.kilde,
            };
            saveEgenkapital(newEgenkapital);
        } else {
            navigate(navigateTo);
        }
    };

    return (
        <PageCard title={egenkapitalTrans.title}>
            <FormProvider {...methods}>
                <form onSubmit={handleSubmit(onSubmitEgenkapital)} className="row row-gap-large">
                    <span className="typography-ingress__light">{egenkapitalTrans.subTitle}</span>
                    <EgenkapitalAnnen />
                    <PageFooter
                        soeknadId={egenkapital?.soeknadId}
                        onForrigeClick={() => navigate(navigateFrom)}
                        showSpinner={isSavingEgenkapital}
                    />
                </form>
            </FormProvider>
        </PageCard>
    );
};

export default Egenkapital;
